import { useState, useCallback, useId, useLayoutEffect, useRef, type ReactNode } from "react";
import { motion } from "framer-motion";
import { Modal } from "../../components/modal";
import { Button } from "../../components/button";
import { InlineAlert } from "../../components/inline-alert";
import { FormField } from "../../components/form-field";
import { EmailInput } from "../../components/email-input";
import { PhoneInput } from "../../components/phone-input";
import { TextInput } from "../../components/text-input";
import { TextArea } from "../../components/text-area";
import { Select } from "../../components/select";
import { CheckboxField } from "../../components/checkbox-field";
import { MultiValueInput } from "../../components/multi-value-input";
import { MultiSelectTags } from "../../components/multi-select-tags";
import { DropdownMultiSelect } from "../../components/dropdown-multi-select";
import { FeedbackFormContext, useFeedbackForm } from "./feedback-form-context";

// ── Root container ──────────────────────────────────────────────

type FeedbackFormProps = {
  open: boolean;
  title: string;
  onClose: () => void;
  onSubmit: (values: Record<string, unknown>) => void;
  submitLabel?: string;
  error?: string;
  children: ReactNode;
};

export function FeedbackForm({
  open,
  title,
  onClose,
  onSubmit,
  submitLabel = "Submit",
  error,
  children,
}: FeedbackFormProps) {
  const [values, setValues] = useState<Record<string, unknown>>({});
  const [shellHeight, setShellHeight] = useState<number | null>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const lastMeasuredHeightRef = useRef<number | null>(null);
  const retryMeasureRef = useRef(false);

  const scheduleMeasureShellHeight = useCallback(() => {
    let frame = 0;
    const measureShellHeight = () => {
      const element = contentRef.current;
      if (!element) return;
      const nextHeight = element.scrollHeight;
      if (
        lastMeasuredHeightRef.current !== null &&
        nextHeight === lastMeasuredHeightRef.current &&
        !retryMeasureRef.current
      ) {
        retryMeasureRef.current = true;
        frame = window.requestAnimationFrame(measureShellHeight);
        return;
      }
      retryMeasureRef.current = false;
      lastMeasuredHeightRef.current = nextHeight;
      setShellHeight(nextHeight);
    };
    frame = window.requestAnimationFrame(measureShellHeight);
    return () => {
      window.cancelAnimationFrame(frame);
    };
  }, []);

  const setValue = useCallback((name: string, value: unknown) => {
    setValues((prev) => ({ ...prev, [name]: value }));
  }, []);

  useLayoutEffect(() => {
    if (!open) return;
    let cancel = scheduleMeasureShellHeight();
    const element = contentRef.current;
    if (!element || typeof ResizeObserver === "undefined") {
      return () => cancel?.();
    }
    const observer = new ResizeObserver(() => {
      cancel?.();
      cancel = scheduleMeasureShellHeight();
    });
    observer.observe(element);
    return () => {
      cancel?.();
      observer.disconnect();
    };
  }, [children, error, open, values, scheduleMeasureShellHeight]);

  const handleClose = useCallback(() => {
    setValues({});
    onClose();
  }, [onClose]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit(values);
    setValues({});
  };

  return (
    <Modal
      open={open}
      title={title}
      onClose={handleClose}
      actions={
        <Button type="submit" form="feedback-form">
          {submitLabel}
        </Button>
      }
    >
      <FeedbackFormContext.Provider value={{ values, setValue }}>
        <form
          data-testid="feedback-form-shell"
          className="cf-feedback-form-shell cf-grid"
          id="feedback-form"
          onSubmit={handleSubmit}
          style={{
            height: shellHeight == null ? "auto" : `${shellHeight}px`,
            overflow: "hidden",
            transition: "height 220ms ease-out",
            willChange: "height",
          }}
        >
          <div ref={contentRef} className="cf-grid">
            {children}
            {error && <InlineAlert tone="danger">{error}</InlineAlert>}
          </div>
        </form>
      </FeedbackFormContext.Provider>
    </Modal>
  );
}

// ── Text / Textarea field ───────────────────────────────────────

type FeedbackTextFieldProps = {
  name: string;
  label: string;
  helpText?: string;
  required?: boolean;
  placeholder?: string;
  multiline?: boolean;
};

export function FeedbackTextField({
  name,
  label,
  helpText,
  required,
  placeholder,
  multiline,
}: FeedbackTextFieldProps) {
  const id = useId();
  const { values, setValue } = useFeedbackForm();
  const value = (values[name] as string) ?? "";

  const Input = multiline ? TextArea : TextInput;
  return (
    <FormField label={label} htmlFor={id} helpText={helpText} required={required}>
      <Input
        id={id}
        name={name}
        value={value}
        required={required}
        placeholder={placeholder}
        onChange={(e) => setValue(name, e.target.value)}
      />
    </FormField>
  );
}

// ── Phone field ────────────────────────────────────────────────

type FeedbackPhoneFieldProps = {
  name: string;
  label: string;
  helpText?: string;
  required?: boolean;
  placeholder?: string;
};

export function FeedbackPhoneField({
  name,
  label,
  helpText,
  required,
  placeholder,
}: FeedbackPhoneFieldProps) {
  const id = useId();
  const { values, setValue } = useFeedbackForm();
  const value = (values[name] as string) ?? "";

  return (
    <FormField label={label} htmlFor={id} helpText={helpText} required={required}>
      <PhoneInput
        id={id}
        name={name}
        value={value}
        required={required}
        placeholder={placeholder}
        onChange={(nextValue) => setValue(name, nextValue)}
      />
    </FormField>
  );
}

// ── Email field ────────────────────────────────────────────────

type FeedbackEmailFieldProps = {
  name: string;
  label: string;
  helpText?: string;
  required?: boolean;
  placeholder?: string;
};

export function FeedbackEmailField({
  name,
  label,
  helpText,
  required,
  placeholder,
}: FeedbackEmailFieldProps) {
  const id = useId();
  const { values, setValue } = useFeedbackForm();
  const value = (values[name] as string) ?? "";

  return (
    <FormField label={label} htmlFor={id} helpText={helpText} required={required}>
      <EmailInput
        id={id}
        name={name}
        value={value}
        required={required}
        placeholder={placeholder}
        onChange={(nextValue) => setValue(name, nextValue)}
      />
    </FormField>
  );
}

// ── Number field ───────────────────────────────────────────────

type FeedbackNumberFieldProps = {
  name: string;
  label: string;
  helpText?: string;
  required?: boolean;
  placeholder?: string;
  min?: number;
  max?: number;
  step?: number;
};

export function FeedbackNumberField({
  name,
  label,
  helpText,
  required,
  placeholder,
  min,
  max,
  step,
}: FeedbackNumberFieldProps) {
  const id = useId();
  const { values, setValue } = useFeedbackForm();
  const value = values[name] as number | string | undefined;

  return (
    <FormField label={label} htmlFor={id} helpText={helpText} required={required}>
      <TextInput
        id={id}
        name={name}
        type="number"
        value={value ?? ""}
        required={required}
        placeholder={placeholder}
        min={min}
        max={max}
        step={step}
        onChange={(e) => {
          const next = e.currentTarget.value;
          const parsed = next === "" ? undefined : e.currentTarget.valueAsNumber;
          setValue(name, parsed !== undefined && Number.isFinite(parsed) ? parsed : undefined);
        }}
      />
    </FormField>
  );
}

// ── Rating field (stars) ────────────────────────────────────────

type FeedbackRatingFieldProps = {
  name: string;
  label: string;
  helpText?: string;
  max?: number;
};

export function FeedbackRatingField({ name, label, helpText, max = 5 }: FeedbackRatingFieldProps) {
  const id = useId();
  const { values, setValue } = useFeedbackForm();
  const current = (values[name] as number) ?? 0;

  return (
    <FormField label={label} htmlFor={id} helpText={helpText}>
      <div className="cf-feedback-rating" id={id} role="radiogroup" aria-label={label}>
        {Array.from({ length: max }, (_, i) => {
          const star = i + 1;
          return (
            <button
              key={star}
              type="button"
              data-active={star <= current}
              aria-label={`${star} of ${max}`}
              onClick={() => setValue(name, star)}
            >
              ★
            </button>
          );
        })}
      </div>
    </FormField>
  );
}

// ── Yes / No toggle with conditional children ───────────────────

type FeedbackToggleFieldProps = {
  name: string;
  label: string;
  helpText?: string;
  children?: ReactNode;
};

export function FeedbackToggleField({ name, label, helpText, children }: FeedbackToggleFieldProps) {
  const id = useId();
  const { values, setValue } = useFeedbackForm();
  const value = values[name] as boolean | undefined;

  return (
    <FormField label={label} htmlFor={id} helpText={helpText}>
      <div className="cf-feedback-toggle" id={id}>
        <Button
          type="button"
          variant={value === true ? "solid" : "outline"}
          tone="brand"
          size="sm"
          onClick={() => setValue(name, true)}
        >
          Yes
        </Button>
        <Button
          type="button"
          variant={value === false ? "solid" : "outline"}
          tone="neutral"
          size="sm"
          onClick={() => setValue(name, false)}
        >
          No
        </Button>
      </div>
      {value === true && children ? (
        <motion.div layout className="cf-feedback-toggle-children">
          {children}
        </motion.div>
      ) : null}
    </FormField>
  );
}

// ── Select field ────────────────────────────────────────────────

type FeedbackSelectFieldProps = {
  name: string;
  label: string;
  helpText?: string;
  required?: boolean;
  placeholder?: string;
  children: ReactNode;
};

export function FeedbackSelectField({
  name,
  label,
  helpText,
  required,
  placeholder,
  children,
}: FeedbackSelectFieldProps) {
  const id = useId();
  const { values, setValue } = useFeedbackForm();
  const value = (values[name] as string) ?? "";

  return (
    <FormField label={label} htmlFor={id} helpText={helpText} required={required}>
      <Select
        id={id}
        name={name}
        value={value}
        required={required}
        onChange={(e) => setValue(name, e.target.value)}
      >
        <option value="">{placeholder ?? "Select..."}</option>
        {children}
      </Select>
    </FormField>
  );
}

// ── Checkbox group (multi-select) ───────────────────────────────

type FeedbackCheckboxGroupProps = {
  name: string;
  label: string;
  helpText?: string;
  options: Array<{ value: string; label: string }>;
};

export function FeedbackCheckboxGroup({
  name,
  label,
  helpText,
  options,
}: FeedbackCheckboxGroupProps) {
  const id = useId();
  const { values, setValue } = useFeedbackForm();
  const selected = (values[name] as string[]) ?? [];

  const toggle = (optionValue: string) => {
    const next = selected.includes(optionValue)
      ? selected.filter((v) => v !== optionValue)
      : [...selected, optionValue];
    setValue(name, next);
  };

  return (
    <FormField label={label} htmlFor={id} helpText={helpText}>
      <div id={id} className="cf-feedback-checkbox-group">
        {options.map((opt) => (
          <CheckboxField
            key={opt.value}
            id={`${id}-${opt.value}`}
            label={opt.label}
            checked={selected.includes(opt.value)}
            onChange={() => toggle(opt.value)}
          />
        ))}
      </div>
    </FormField>
  );
}

// ── Multi-value (free-text list) ────────────────────────────────

type FeedbackMultiValueFieldProps = {
  name: string;
  label: string;
  helpText?: string;
  placeholder?: string;
};

export function FeedbackMultiValueField({
  name,
  label,
  helpText,
  placeholder,
}: FeedbackMultiValueFieldProps) {
  const id = useId();
  const { values, setValue } = useFeedbackForm();
  const items = (values[name] as string[]) ?? [""];

  return (
    <FormField label={label} htmlFor={id} helpText={helpText}>
      <MultiValueInput
        id={id}
        values={items}
        placeholder={placeholder}
        onChange={(next) => setValue(name, next)}
      />
    </FormField>
  );
}

// ── Multi-select tags (fixed options, toggle buttons) ────────────

type FeedbackMultiSelectTagsFieldProps = {
  name: string;
  label: string;
  helpText?: string;
  options: Array<{ value: string; label: string }>;
};

export function FeedbackMultiSelectTagsField({
  name,
  label,
  helpText,
  options,
}: FeedbackMultiSelectTagsFieldProps) {
  const id = useId();
  const { values, setValue } = useFeedbackForm();
  const selected = (values[name] as string[]) ?? [];

  return (
    <FormField label={label} htmlFor={id} helpText={helpText}>
      <MultiSelectTags
        id={id}
        values={selected}
        options={options.map((o) => o.label)}
        onChange={(next) => setValue(name, next)}
      />
    </FormField>
  );
}

// ── Dropdown multi-select (searchable dropdown + tags) ───────────

type FeedbackDropdownMultiSelectFieldProps = {
  name: string;
  label: string;
  helpText?: string;
  options: Array<{ value: string; label: string }>;
  placeholder?: string;
};

export function FeedbackDropdownMultiSelectField({
  name,
  label,
  helpText,
  options,
  placeholder,
}: FeedbackDropdownMultiSelectFieldProps) {
  const id = useId();
  const { values, setValue } = useFeedbackForm();
  const selected = (values[name] as string[]) ?? [];

  return (
    <DropdownMultiSelect
      id={id}
      label={label}
      helpText={helpText}
      values={selected}
      options={options}
      addLabel="Add"
      emptyLabel={placeholder ?? "Nothing selected."}
      onChange={(next) => setValue(name, next)}
    />
  );
}
